/**
 * DownloadAppContent — Shared content for "Download the mobile app" screens.
 * Used in two contexts:
 *   1. Full-page: shown to team members after signup (AuthPage)
 *   2. Dialog: shown to admins/owners from the settings menu (Dashboard)
 *
 * Props:
 *   email (string|null) — If provided, shows "Sign in with email@..." hint
 *   variant ("page"|"dialog") — Controls sizing/spacing
 */
import React, { useMemo, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone } from 'lucide-react';
import { useDesign } from '@/contexts/DesignContext';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=app.emergent.presubmissionprep519256ce';
const APPLE_STORE_URL = 'https://apps.apple.com/us/app/worship-os/id6760857865';
// v1.0.6 installers — canonical downloads (a newer GitHub release asset replaces these at runtime)
const MAC_DMG_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_815b4f96-0dec-4819-86b5-281c7bd2f431/artifacts/qcwlrprk_Worship.OS_1.0.6_universal.dmg';
const WINDOWS_EXE_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_815b4f96-0dec-4819-86b5-281c7bd2f431/artifacts/uozpepzc_Worship.OS_1.0.6_x64-setup.exe';
const RELEASES_API = 'https://api.github.com/repos/alexthearmo/worship-os-releases/releases/latest';
const FALLBACK_VERSION = '1.0.6';

// Extract "1.0.6" from names like "Worship.OS_1.0.6_universal.dmg"
const versionOf = (name) => (name.match(/(\d+\.\d+\.\d+)/) || [])[1] || '0.0.0';
const newerThan = (a, b) => a.localeCompare(b, undefined, { numeric: true }) > 0;
// Pick the highest-versioned asset with the given extension
const pickNewest = (assets, ext) => {
  const matches = assets.filter((a) => a.name.endsWith(ext));
  if (!matches.length) return null;
  matches.sort((a, b) => (newerThan(versionOf(a.name), versionOf(b.name)) ? -1 : 1));
  return matches[0];
};

// Simple Apple icon (SF Symbols style)
function AppleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

// Google Play triangle icon
function PlayStoreIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.18 1.42c-.18.2-.18.5-.18.82v19.52c0 .32 0 .62.18.82L3.2 22.6l10.47-10.47v-.26L3.2 1.4zM17.1 16L13.67 12.13v-.26L17.1 8l.05.03 4.08 2.32c1.17.66 1.17 1.75 0 2.41l-4.08 2.32zM13.67 12.13L3.2 22.6c.6.63 1.58.07 1.58.07l12.27-6.98zM3.2 1.4s-.98-.56-1.58.07l10.47 10.47L16.44 8l-.05-.03L3.2 1.4z"/>
    </svg>
  );
}

// Google Play, the store's own four-colour artwork (Studio store badge only)
function PlayStoreColorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path fill="#00D7FE" d="M3.6 2.6 13 12 3.6 21.4c-.4-.3-.6-.8-.6-1.4V4c0-.6.2-1.1.6-1.4Z"/>
      <path fill="#FFCE00" d="m13 12 3.2-3.2 4.3 2.4c1.1.6 1.1 1.6 0 2.2l-4.3 2.4Z"/>
      <path fill="#FF3A44" d="m13 12 3.2 3.2-11.4 6.5c-.5.3-1 .3-1.4 0Z"/>
      <path fill="#48FF48" d="m13 12 3.2-3.2L4.8 2.3c-.4-.3-.9-.3-1.4 0Z"/>
    </svg>
  );
}

// Windows logo (four panes)
function WindowsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 5.55l7.36-1.01v7.1H3V5.55zm0 12.9l7.36 1.01v-7.02H3v6.01zm8.17 1.12L21 21V12.44h-9.83v7.13zm0-15.24v7.31H21V3l-9.83 1.33z"/>
    </svg>
  );
}

export default function DownloadAppContent({ email = null, variant = 'page' }) {
  // Bundled v1.0.5 installers are the default; a strictly-newer GitHub
  // release asset replaces them at runtime.
  const [desktopUrls, setDesktopUrls] = useState({ mac: MAC_DMG_URL, win: WINDOWS_EXE_URL });
  useEffect(() => {
    fetch(RELEASES_API)
      .then((r) => (r.ok ? r.json() : null))
      .then((rel) => {
        if (!rel?.assets?.length) return;
        const mac = pickNewest(rel.assets, '.dmg');
        const win = pickNewest(rel.assets, '.exe');
        setDesktopUrls((prev) => ({
          mac: mac && newerThan(versionOf(mac.name), FALLBACK_VERSION) ? mac.browser_download_url : prev.mac,
          win: win && newerThan(versionOf(win.name), FALLBACK_VERSION) ? win.browser_download_url : prev.win,
        }));
      })
      .catch(() => {});
  }, []);

  const isMobile = useMemo(() => {
    const ua = navigator.userAgent || '';
    return /iPhone|iPod|Android/i.test(ua) && window.innerWidth < 1024;
  }, []);

  const isPage = variant === 'page';
  const { isStudio } = useDesign();

  // ---- Studio (doc 119 §24, mock frames 20, 21, 27) ----
  // The store badges are the stores' own artwork. On the desktop pair they are
  // NOT links — classic draws a plain text label there and R3 keeps control
  // parity; on the phone they carry classic's two anchors and testids.
  if (isStudio) {
    const badge = (store, wide) => (
      <span className={`st-au-badge${wide ? ' st-au-wide' : ''}`}>
        {store === 'ios' ? <AppleIcon className="w-[26px] h-[26px]" /> : <PlayStoreColorIcon className="w-6 h-6" />}
        <span className="st-au-bt">
          <small>{store === 'ios' ? 'Download on the' : 'GET IT ON'}</small>
          <b>{store === 'ios' ? 'App Store' : 'Google Play'}</b>
        </span>
      </span>
    );
    return (
      <div className="st-au-gatebody">
        <div className="st-au-head st-au-c" style={{ marginBottom: 0 }}>
          <h2 className="st-au-t">{isPage ? 'Welcome to Worship OS!' : 'Download apps'}</h2>
          <p className="st-au-s">
            {isPage
              ? 'Download the app and sign in to get started with your team.'
              : 'Get Worship OS on your phone or computer.'}
            {isPage && email && <><br /><span style={{ display: 'inline-block', marginTop: '6px' }}>Sign in with <b>{email}</b></span></>}
          </p>
        </div>

        {isMobile ? (
          <div className="st-au-stores">
            <a href={APPLE_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="download-ios-btn">
              {badge('ios', true)}
            </a>
            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" data-testid="download-android-btn">
              {badge('android', true)}
            </a>
          </div>
        ) : (
          <div className="st-au-qrs">
            <div>
              <span className="st-au-qr"><QRCodeSVG value={APPLE_STORE_URL} size={140} level="M" /></span>
              {badge('ios')}
            </div>
            <div>
              <span className="st-au-qr"><QRCodeSVG value={GOOGLE_PLAY_URL} size={140} level="M" /></span>
              {badge('android')}
            </div>
          </div>
        )}

        <div className="st-au-eyebrowdiv">Desktop app</div>

        <div className={`st-au-desk${isMobile ? ' st-au-col' : ''}`}>
          <a
            href={desktopUrls.mac}
            target="_blank"
            rel="noopener noreferrer"
            className="st-au-btn st-au-pri"
            data-testid="download-mac-btn"
          >
            <AppleIcon className="w-4 h-4" />
            <span>Download for Mac</span>
          </a>
          <a
            href={desktopUrls.win}
            target="_blank"
            rel="noopener noreferrer"
            className="st-au-btn st-au-pri"
            data-testid="download-windows-btn"
          >
            <WindowsIcon className="w-4 h-4" />
            <span>Download for Windows</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center text-center ${isPage ? 'px-6' : 'px-2'}`}>
      {/* Icon — the dialog variant has no tile above its title under Studio */}
      {!(isStudio && !isPage) && (
      <div data-ui="brand-tile" className={`rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg ${isPage ? 'w-16 h-16 mb-5' : 'w-12 h-12 mb-4'}`}>
        <Smartphone className={`text-white ${isPage ? 'w-8 h-8' : 'w-6 h-6'}`} />
      </div>
      )}

      {/* Heading */}
      <h2 className={isStudio
        ? `font-semibold text-foreground ${isPage ? 'text-xl mb-2' : 'text-[17px] mb-1'}`
        : `font-bold text-slate-800 dark:text-slate-100 ${isPage ? 'text-2xl mb-2' : 'text-xl mb-1.5'}`}>
        {isPage ? 'Welcome to Worship OS!' : (isStudio ? 'Download apps' : 'Download Apps')}
      </h2>

      {/* Subtext */}
      <p className={`text-slate-500 dark:text-slate-400 max-w-sm ${isPage ? 'text-base mb-1' : (isStudio ? 'text-[13.5px] mb-1' : 'text-sm mb-1')}`}>
        {isPage
          ? 'Download the app and sign in to get started with your team.'
          : 'Get Worship OS on your phone or computer.'}
      </p>

      {/* Email hint */}
      {email && (
        <p className={`text-slate-400 dark:text-slate-500 ${isPage ? 'text-sm mb-8' : 'text-xs mb-6'}`}>
          Sign in with <span className="font-medium text-slate-600 dark:text-slate-300">{email}</span>
        </p>
      )}
      {!email && <div className={isPage ? 'mb-8' : 'mb-6'} />}

      {/* Mobile: Buttons */}
      {isMobile ? (
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <a
            href={APPLE_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full h-14 bg-slate-900 hover:bg-black text-white font-medium rounded-xl transition-colors shadow-md"
            data-testid="download-ios-btn"
          >
            <AppleIcon className="w-6 h-6" />
            <div className="text-left">
              <div className="text-[10px] leading-tight opacity-80">Download on the</div>
              <div className="text-base font-semibold leading-tight">App Store</div>
            </div>
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full h-14 bg-slate-900 hover:bg-black text-white font-medium rounded-xl transition-colors shadow-md"
            data-testid="download-android-btn"
          >
            <PlayStoreIcon className="w-5 h-5" />
            <div className="text-left">
              <div className="text-[10px] leading-tight opacity-80">Get it on</div>
              <div className="text-base font-semibold leading-tight">Google Play</div>
            </div>
          </a>
        </div>
      ) : (
        /* Desktop: QR Codes */
        <div className="flex gap-8">
          <div className="flex flex-col items-center">
            <div className={isStudio ? 'p-2 bg-card rounded-xl border border-border mb-2' : 'p-3 bg-card rounded-xl border border-slate-200 dark:border-white/10 shadow-sm mb-3'}>
              <QRCodeSVG value={APPLE_STORE_URL} size={140} level="M" />
            </div>
            <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 font-medium">
              <AppleIcon className="w-4 h-4" />
              App Store
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className={isStudio ? 'p-2 bg-card rounded-xl border border-border mb-2' : 'p-3 bg-card rounded-xl border border-slate-200 dark:border-white/10 shadow-sm mb-3'}>
              <QRCodeSVG value={GOOGLE_PLAY_URL} size={140} level="M" />
            </div>
            <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 font-medium">
              <PlayStoreIcon className="w-4 h-4" />
              Google Play
            </div>
          </div>
        </div>
      )}

      {/* Desktop apps (Mac + Windows) */}
      <div className={`w-full max-w-xs ${isPage ? 'mt-8' : 'mt-6'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-slate-200 dark:bg-white/10" />
          <span className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">{isStudio ? 'Desktop app' : 'Desktop App'}</span>
          <div className="flex-1 h-px bg-slate-200 dark:bg-white/10" />
        </div>
        <div className="flex gap-3">
          <a
            href={desktopUrls.mac}
            target="_blank"
            rel="noopener noreferrer"
            className={isStudio
              ? 'flex items-center justify-center gap-2 flex-1 h-11 bg-primary hover:opacity-90 text-primary-foreground font-medium rounded-lg text-[14px] transition-colors'
              : "flex items-center justify-center gap-2.5 flex-1 h-12 bg-slate-900 hover:bg-black text-white font-medium rounded-xl transition-colors shadow-md"}
            data-testid="download-mac-btn"
          >
            <AppleIcon className={isStudio ? 'w-4 h-4' : 'w-5 h-5'} />
            {isStudio ? (
              <span>Download for Mac</span>
            ) : (
            <div className="text-left">
              <div className="text-[10px] leading-tight opacity-80">Download for</div>
              <div className="text-sm font-semibold leading-tight">Mac</div>
            </div>
            )}
          </a>
          <a
            href={desktopUrls.win}
            target="_blank"
            rel="noopener noreferrer"
            className={isStudio
              ? 'flex items-center justify-center gap-2 flex-1 h-11 bg-primary hover:opacity-90 text-primary-foreground font-medium rounded-lg text-[14px] transition-colors'
              : "flex items-center justify-center gap-2.5 flex-1 h-12 bg-slate-900 hover:bg-black text-white font-medium rounded-xl transition-colors shadow-md"}
            data-testid="download-windows-btn"
          >
            <WindowsIcon className={isStudio ? 'w-4 h-4' : 'w-5 h-5'} />
            {isStudio ? (
              <span>Download for Windows</span>
            ) : (
            <div className="text-left">
              <div className="text-[10px] leading-tight opacity-80">Download for</div>
              <div className="text-sm font-semibold leading-tight">Windows</div>
            </div>
            )}
          </a>
        </div>
      </div>
    </div>
  );
}
